import type {
  CellQueryRecord,
  CubeHellAction,
  CubeHellPlayerId,
  CubeHellState,
  GuessAction,
  GuessRecord,
  LayerQueryRecord,
  QueryCellAction,
  QueryLayerAction,
  QueryRecord,
} from "../types";
import { CELL_COUNT } from "../types";
import {
  countMatches,
  getOpponentPlayerId,
  queryCell,
  queryLayer,
} from "../utils";

/** Append a query/guess record and bump the move counter. */
function withRecord(
  state: CubeHellState,
  action: CubeHellAction,
  record: QueryRecord,
): CubeHellState {
  return {
    ...state,
    log: [...state.log, record],
    lastAction: action,
    moveNumber: state.moveNumber + 1,
  };
}

/** A query consumes the actor's budget and passes the turn to the opponent. */
function afterQuery(
  state: CubeHellState,
  asker: CubeHellPlayerId,
): Partial<CubeHellState> {
  const opponent = getOpponentPlayerId(asker);
  return {
    queryCounts: {
      ...state.queryCounts,
      [asker]: state.queryCounts[asker] + 1,
    },
    currentPlayerId: opponent,
  };
}

export function applyQueryCell(
  state: CubeHellState,
  action: QueryCellAction,
): CubeHellState {
  const asker = action.playerId;
  const target = getOpponentPlayerId(asker);
  const record: CellQueryRecord = {
    kind: "cell",
    seq: state.log.length + 1,
    askerId: asker,
    targetId: target,
    x: action.x,
    y: action.y,
    z: action.z,
    result: queryCell(state.arrangements[target], action.x, action.y, action.z),
  };
  return { ...withRecord(state, action, record), ...afterQuery(state, asker) };
}

export function applyQueryLayer(
  state: CubeHellState,
  action: QueryLayerAction,
): CubeHellState {
  const asker = action.playerId;
  const target = getOpponentPlayerId(asker);
  const record: LayerQueryRecord = {
    kind: "layer",
    seq: state.log.length + 1,
    askerId: asker,
    targetId: target,
    axis: action.axis,
    index: action.index,
    color: action.color,
    count: queryLayer(
      state.arrangements[target],
      action.axis,
      action.index,
      action.color,
    ),
  };
  return { ...withRecord(state, action, record), ...afterQuery(state, asker) };
}

export function applyGuess(
  state: CubeHellState,
  action: GuessAction,
): CubeHellState {
  const asker = action.playerId;
  const target = getOpponentPlayerId(asker);
  const matched = countMatches(action.arrangement, state.arrangements[target]);
  const correct = matched === CELL_COUNT;
  const record: GuessRecord = {
    kind: "guess",
    seq: state.log.length + 1,
    askerId: asker,
    targetId: target,
    correct,
    matched,
  };
  const base = withRecord(state, action, record);

  if (correct) {
    return { ...base, phase: "game-over", winnerId: asker };
  }
  if (state.guessPenalty === "immediate-loss") {
    return { ...base, phase: "game-over", winnerId: target };
  }
  // turn-loss: a wrong guess only forfeits the turn.
  return { ...base, currentPlayerId: target };
}

export function applyAction(
  state: CubeHellState,
  action: CubeHellAction,
): CubeHellState {
  switch (action.type) {
    case "QueryCell":
      return applyQueryCell(state, action);
    case "QueryLayer":
      return applyQueryLayer(state, action);
    case "Guess":
      return applyGuess(state, action);
  }
}
